<template>
  <div
    :class="{
      'profile-flyout-chat-box-fi': true,
      'profile-flyout-chat-box-fi--open': isExpanded,
    }"
  >
    <button
      type="button"
      class="profile-flyout-chat-box-fi__head"
      :aria-expanded="isExpanded"
      @click="toggle"
    >
      <icon-fi
        icon="message-square"
        class="profile-flyout-chat-box-fi__head__icon"
      />
      <div class="profile-flyout-chat-box-fi__head__text">
        <div class="profile-flyout-chat-box-fi__head__headline">
          {{ sitecoreData.CopilotHeadline }}
        </div>
        <div class="profile-flyout-chat-box-fi__head__subheadline">
          {{ sitecoreData.CopilotSubheadline }}
        </div>
      </div>

      <div
        v-if="!isExpanded"
        class="profile-flyout-chat-box-fi__head__button"
      >
        <span> {{ sitecoreData.CopilotButtonText }}</span>
        <icon-fi icon="arrow-right" />
      </div>

      <div
        v-else
        :class="{
          'profile-flyout-chat-box-fi__head__refresh': true,
          'profile-flyout-chat-box-fi__head__refresh--spinning': isRefreshing,
        }"
        @click.stop="refresh"
        @animationend="isRefreshing = false"
      >
        <icon-fi icon="load-loop" />
      </div>

      <icon-fi
        icon="chevron-down"
        class="profile-flyout-chat-box-fi__head__toggle"
      />
    </button>

    <div
      :class="{
        'profile-flyout-chat-box-fi__content': true,
        'profile-flyout-chat-box-fi__content--open': isExpanded,
      }"
      @transitionend="onContentTransitionEnd"
    >
      <div class="profile-flyout-chat-box-fi__content__wrapper">
        <chat-box-fi
          v-if="hasOpened"
          ref="chatBox"
          :backend="backend"
          @decline="isExpanded = false"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { IconFi } from 'atoms';
import { nextTick, ref, useTemplateRef } from 'vue';

import { useWebSocketChatBackend } from '@/components/chat-box-fi/backends/useWebSocketChatBackend';
import ChatBoxFi from '@/components/chat-box-fi/ChatBoxFi.vue';

import type { ProfileFlyoutData } from '../profile-flyout-types';

defineOptions({ name: 'ProfileFlyoutChatBoxFi' });

defineProps<{
  sitecoreData: ProfileFlyoutData;
}>();

const backend = useWebSocketChatBackend();

const chatBoxRef = useTemplateRef('chatBox');

const isExpanded = ref<boolean>(false);

const hasOpened = ref<boolean>(false);
const isRefreshing = ref<boolean>(false);

const toggle = () => {
  isExpanded.value = !isExpanded.value;
  if (isExpanded.value) {
    hasOpened.value = true;
  }
};

const onContentTransitionEnd = (event: TransitionEvent) => {
  if (event.target !== event.currentTarget || event.propertyName !== 'grid-template-rows') {
    return;
  }
  if (isExpanded.value) {
    chatBoxRef.value?.focusInput();
  }
};

const refresh = async () => {
  isRefreshing.value = true;
  chatBoxRef.value?.restart();

  await nextTick();
  chatBoxRef.value?.focusInput();
};
</script>

<style scoped src="./profile-flyout-chat-box-fi" />
